import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import axios from "axios";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { BiTime } from "react-icons/bi";
import {
  FaCogs,
  FaEnvelope,
  FaFileUpload,
  FaFolderOpen,
  FaFolderPlus,
  FaHeadSideVirus,
  FaObjectGroup,
  FaPlay,
  FaSpinner,
  FaSync,
  FaUser,
  FaUserFriends,
  FaUserPlus,
  FaWaveSquare,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import api from "../api";
import { IEventResponse } from "../api/services/event.service";
import { IUserResponse } from "../api/services/user.service";
import { useAuth } from "../hooks/useAuth";
import { usePagination } from "../hooks/usePagination";
import Pagination from "./shared/Pagination";
import { openFileInNewTab } from "../api/utils";

export default function EventSection({
  userId,
  teamId,
  pageSize,
}: {
  userId?: string;
  teamId?: string;
  pageSize?: number;
}) {
  const { user } = useAuth();

  const [events, setEvents] = useState<IEventResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { page, paginationProps, searchTerm, updatePagination, setPage } =
    usePagination();

  useEffect(() => {
    search(searchTerm, page);
  }, [searchTerm, page]);

  async function search(searchTerm: string | null, page: number | null) {
    try {
      setLoading(true);
      const criteria = [];
      if (userId) {
        criteria.push(
          {
            key: "headers.userId",
            operation: ":",
            value: userId,
          },
          {
            key: "result.userId",
            operation: ":",
            value: userId,
          },
          {
            key: "result.id",
            operation: ":",
            value: userId,
          }
        );
      }
      if (teamId) {
        criteria.push(
          {
            key: "headers.teamId",
            operation: ":",
            value: teamId,
          },
          {
            key: "result.teamId",
            operation: ":",
            value: teamId,
          },
          {
            key: "result.id",
            operation: ":",
            value: teamId,
          }
        );
      }
      // flags: [
      //   {
      //     key: "result.projectId",
      //     operation: ":",
      //     value: "f79bcf59-392e-44fd-bb4f-81dbff14d9f1",
      //   },
      // ],
      const { data } = await api.event.searchEvents({
        term: searchTerm,
        criteria,
        page,
        size: pageSize || 10,
      });
      if (data.result) {
        const pageContent = data.result.content;
        setEvents(pageContent);
        updatePagination(data);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function handleEventClone(event: IEventResponse) {
    event.headers.rerun = true;
    event.headers.rerunUserId = user?.id;
    event.headers.rerunEventId = event.id;
    event.createdAt = new Date();
    event.id = uuidv4();
    try {
      const { status } = await api.event.createEvent(event);
      if (status === 201) {
        toast.success("Event submitted for re-run");
        setTimeout(() => search(searchTerm, 0), 2000);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function EventRow({ event }: { event: IEventResponse }) {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState<IUserResponse | null>(null);

    useEffect(() => {
      if (open && !userId) {
        const targetUser =
          event.headers.userId === "ANONYMOUS"
            ? event.result.id
            : event.headers.userId;
        fetchUser(targetUser);
      }
    }, [open]);

    async function fetchUser(userId: string) {
      try {
        // setLoading(true);
        const { data } = await api.user.getUser(userId);
        if (data.result) {
          setUser(data.result);

          // setLoading(false);
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          if (e.response?.data) {
            toast.error(e.response?.data.errorMessage);
          }
        } else {
          console.log(e);
          toast.error(
            "Something really went wrong, you might want to contact support!"
          );
        }
      }
    }

    async function openProjectInNewTab(projectId: string) {
      window.open(`https://app.ktr.studio/studio/${projectId}`, "_blank");
    }

    function eventToIcon(event: IEventResponse) {
      switch (event.name) {
        case "USER_LOGIN":
          return <FaUser className="h-10 w-10 text-slate-400" />;
        case "USER_SIGNUP":
          return <FaUserPlus className="h-10 w-10 text-slate-400" />;
        case "PROJECT_CREATED":
          return <FaFolderPlus className="h-10 w-10 text-slate-400" />;
        case "PROJECT_MEMBER_JOINED":
          return <FaUserFriends className="h-10 w-10 text-slate-400" />;
        case "FILE_CREATED":
          return <FaFileUpload className="h-10 w-10 text-slate-400" />;
        case "PROJECT_MEMBER_INVITED_EXTERNAL":
          return <FaEnvelope className="h-10 w-10 text-slate-400" />;
        case "PROJECT_MEMBER_INVITED_INTERNAL":
          return <FaEnvelope className="h-10 w-10 text-slate-400" />;
        default:
          return <FaWaveSquare className="h-10 w-10 text-slate-400" />;
      }
    }
    return (
      <div className="break-all py-4 px-4">
        <div className="flex items-center" onClick={() => setOpen(!open)}>
          <div className="min-w-0 flex-1 flex items-start">
            <div className="min-w-0 flex-1 md:grid md:grid-cols-2 md:gap-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-3">{eventToIcon(event)}</div>
                <div>
                  <p className="text-sm font-medium text-red-600 break-all">
                    {event.name}
                  </p>
                  <p className="mt-2 flex items-center text-sm text-gray-500">
                    <BiTime
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="truncate">
                      {format(new Date(event.createdAt), "dd.MM.yyyy HH:mm:ss")}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mr-2">
            {!open && (
              <ChevronDownIcon
                className="h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
            )}
            {open && (
              <ChevronUpIcon
                className="h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
            )}
          </div>
        </div>
        <div
          className={`flex flex-col md:flex-row items-start space-y-6 md:space-y-0 py-4 md:space-x-4 ${
            open ? "" : "hidden"
          }`}
        >
          <div className="md:w-1/2">
            <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4">
              <FaCogs />
              <div>Actions</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <button
                  onClick={() => handleEventClone(event)}
                  type="button"
                  className="bg-red-700 p-2 text-red-400 rounded-xl hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FaSync className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="ml-2 text-slate-700 font-medium">
                  Re-run event
                </div>
              </div>
              {event.name === "FILE_CREATED" && (
                <div className="flex items-center">
                  <button
                    onClick={() => openFileInNewTab(event.result.id, event.result.tenantId)}
                    type="button"
                    className="bg-red-700 p-2 text-red-400 rounded-xl hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <FaPlay className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div className="ml-2 text-slate-700 font-medium">
                    Start Playback
                  </div>
                </div>
              )}
              {event.name === "PROJECT_CREATED" && (
                <div className="flex items-center">
                  <button
                    onClick={() => openProjectInNewTab(event.result.id)}
                    type="button"
                    className="bg-red-700 p-2 text-red-400 rounded-xl hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <FaFolderOpen className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div className="ml-2 text-slate-700 font-medium">
                    Goto Project
                  </div>
                </div>
              )}
            </div>

            {user && (
              <>
                <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4 mt-6">
                  <FaUser />
                  <div>User</div>
                </div>
                <div className="flex flex-col space-y-3">
                  <div>
                    <div>Name</div>
                    <div className="text-slate-700 font-medium">
                      {user.firstname} {user.lastname}
                    </div>
                  </div>
                  <div>
                    <div>Email</div>
                    <div className="text-slate-700 font-medium">
                      {user.email}
                    </div>
                  </div>
                  <div>
                    <div>Primary Id</div>
                    <div className="text-slate-700 font-medium">{user.id}</div>
                  </div>
                  <Link
                    to={`/user/${user.id}`}
                    className="relative flex justify-center py-2 px-4 border border-transparent text-sm font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mb-2"
                  >
                    Open
                  </Link>
                </div>
              </>
            )}
          </div>
          <div className="md:w-1/2">
            <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4">
              <FaHeadSideVirus />
              <div>Headers</div>
            </div>
            <div className="md:hidden text-sm text-gray-900 font-mono">
              {JSON.stringify(event.headers, null, 2)}
            </div>
            <pre className="hidden md:block text-sm font-mono bg-slate-200 text-slate-700 p-4 rounded-lg">
              {JSON.stringify(event.headers, null, 2)}
            </pre>
            <div className="flex font-semibold text-red-600 space-x-1 items-center mb-4 mt-6">
              <FaObjectGroup />
              <div>Payload</div>
            </div>
            <div className="md:hidden text-sm text-gray-900 font-mono">
              {JSON.stringify(event.result, null, 2)}
            </div>
            <pre className="hidden md:block text-sm font-mono bg-slate-200 text-slate-700 p-4 rounded-lg">
              {JSON.stringify(event.result, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow ">
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaWaveSquare size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex items-center">
              Events
            </span>
          </div>
        </div>
        <div className=""></div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && events.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden">
          {events.map((event, i) => (
            <EventRow key={i} event={event} />
          ))}
        </div>
      )}
      {!loading && events.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            No Events Found
          </div>
        </div>
      )}
      <Pagination {...paginationProps} goToPage={setPage} />
    </div>
  );
}
